import { UserRole } from "@/store/authStore";

/**
 * Checks if the user has one of the specified roles
 * @param userRole Current user role
 * @param allowedRoles Roles that are allowed
 */
export const hasRole = (
  userRole: UserRole | undefined,
  allowedRoles: UserRole[]
): boolean => {
  if (!userRole) return false;
  return allowedRoles.includes(userRole);
};

/**
 * Checks if the user has admin privileges (manager or super_admin)
 * @param userRole Current user role
 */
export const isAdminRole = (userRole: UserRole | undefined): boolean => {
  return hasRole(userRole, ["manager", "super_admin"]);
};

/**
 * Checks if the user has staff privileges (admin, manager, or staff)
 * @param userRole Current user role
 */
export const isStaffRole = (userRole: UserRole | undefined): boolean => {
  return hasRole(userRole, ["staff", "admin", "manager", "super_admin"]);
};

/**
 * Checks if the user is a super admin
 * @param userRole Current user role
 */
export const isSuperAdmin = (userRole: UserRole | undefined): boolean => {
  return userRole === "super_admin";
};

/**
 * Gets the allowed navigation items for a specific role
 * @param userRole Current user role
 */
export const getAllowedNavItems = (
  userRole: UserRole | undefined
): string[] => {
  if (!userRole) return [];

  switch (userRole) {
    case "super_admin":
    case "manager":
      return ["all"]; // Special case - allows all routes
    case "admin":
      return [
        "dashboard",
        "tables",
        "orders",
        "history",
        "activity",
        "notifications",
        "menu",
        "inventory",
        "menu-sandbox",
        "chat",
        "staff",
        "attendance",
        "beauty-services",
        "storefront",
      ];
    case "staff":
      return ["orders", "activity", "notifications", "chat", "attendance"];
    default:
      return [];
  }
};

/**
 * Checks if a specific navigation item is allowed for the user role
 * @param userRole Current user role
 * @param navItem Navigation item to check
 */
export const isNavItemAllowed = (
  userRole: UserRole | undefined,
  navItem: string
): boolean => {
  if (!userRole) return false;
  const allowedItems = getAllowedNavItems(userRole);
  return allowedItems.includes("all") || allowedItems.includes(navItem);
};
